import { type AfterViewInit, Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { MatDatepicker } from '@angular/material/datepicker';
import moment from 'moment';
import type { Moment } from 'moment';
import { FormField } from '../../models/form-field';
import { InputType, OptionInputType } from '../../models/input-type.enum';
import { MonekColour } from '../../models/monek-colour.enum';
import { DatePipe } from '@angular/common';
import { FormOption } from '../../models/form-option';
import { MatSelectChange } from '@angular/material/select';

@Component({
  standalone: false,
  selector: 'odin-dynamic-input',
  templateUrl: './dynamic-input.component.html',
  styleUrls: ['./dynamic-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [],
})
export class DynamicInputComponent implements AfterViewInit {
  @Input() public theme: MonekColour = MonekColour.Primary;
  @Input() public errorTheme: MonekColour = MonekColour.Warn;
  @Input() public formField?: FormField;
  @Input() public disabledInput: boolean = false;
  @Output() public valueChanged: EventEmitter<
    number | string | Date | boolean | null
  > = new EventEmitter<number | string | Date | boolean | null>();

  public required: boolean = false;
  private cleanInput = true;
  public dateInput = new Date();

  constructor(private datePipe: DatePipe) {}

  public ngAfterViewInit(): void {
    this.required = this.formField?.validation.isRequired() || false;
    this.formField?.valueChanged.subscribe((val: number | string | Date | boolean | null) => {
      this.CheckValidation();
    })
  }

  public InputMode(): string {
    if (!this.formField) return 'text';
    if (this.formField.type == InputType.Number) return 'numeric';

    return 'text';
  }

  public SetValue(option: FormOption): void {
    if (this.formField === undefined) return;
    this.formField.value = option.value;
    this.CheckValidation();
    this.formField.onBlur(this.formField, option.value);
  }

  public DropdownChange(change: MatSelectChange): void {
    if (this.formField === undefined) return;
    this.formField.value = change.value;
    this.CheckValidation();
    this.formField.onBlur(this.formField, change.value);
  }

  /**
   * Strips anything that is not a digit or a single decimal point, so a value
   * such as "£56.51" or "1,234.50" becomes "56.51" / "1234.50". Merchants
   * typing a currency symbol were previously left with an unparseable value
   * that the blur formatter zeroed out. A leading minus is kept ("-5" stays
   * "-5") so fields that accept negative values still can.
   *
   * While the user is still typing, a lone "-" has to survive too: sanitising
   * on every keystroke would otherwise drop it, so "-" followed by "5" would
   * become a positive "5" (and could pass validation on a payment amount).
   * Pass `keepLoneMinus` for incremental input; on blur it is discarded.
   */
  public static SanitiseNumber(
    value: string | number | boolean | Date | null,
    keepLoneMinus: boolean = false,
  ): string {
    if (value === null || value === undefined) return '';
    const raw = value.toString().trim();
    const sign = raw.startsWith('-') ? '-' : '';
    if (keepLoneMinus && sign && !/[0-9]/.test(raw)) return '-';
    let cleaned = raw.replace(/[^0-9.]/g, '');
    const firstDot = cleaned.indexOf('.');
    if (firstDot !== -1) {
      cleaned =
        cleaned.slice(0, firstDot + 1) +
        cleaned.slice(firstDot + 1).replace(/\./g, '');
    }
    return cleaned ? sign + cleaned : '';
  }

  public OnInput(): void {
    if (this.formField && this.formField.type === InputType.Number) {
      const cleaned = DynamicInputComponent.SanitiseNumber(this.formField.value, true);
      if (cleaned !== this.formField.value) this.formField.value = cleaned;
    }
    this.CheckValidation();
  }

  public OnBlur(rawValue: string): void {
    if (!this.formField) return;
    const value =
      this.formField.type === InputType.Number
        ? DynamicInputComponent.SanitiseNumber(rawValue)
        : rawValue;
    this.formField.onBlur(this.formField, value);
  }

  public CheckValidation(): void {
    this.cleanInput = false;
    this.valueChanged.emit();

    if (!this.formField) return;
    
    this.formField.onChange(
      this.formField.value,
      this.formField.data,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (data: any) => this.UpdateData(data),
      (val: string | number | boolean | Date | null) => this.UpdateVal(val),
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private UpdateData(data: any): void {}

  private UpdateVal(data: string | number | boolean | Date | null): void {
    if (this.formField) {
      this.formField.value = data;
    }
    this.valueChanged.emit(data);
  }

  public Validate(): boolean {
    if (this.cleanInput) return true;
    if (this.formField == null || typeof this.formField.value !== 'string')
      return false;
    const validationResult = this.formField.validation.Validate(
      this.formField.value,
    );
    return validationResult.validationOutcome;
  }

  public GetValidationErrors(): string {
    if (this.formField == null || typeof this.formField.value !== 'string')
      return '';
    const errors = this.formField.validation.Errors(this.formField.value);
    if (errors == null || errors.length == 0) return 'Invalid';
    return errors[0];
  }

  public isDropdown(inputType: InputType | OptionInputType): boolean {
    if (inputType == OptionInputType.Dropdown)
      return true;
    return false;
  }

  public isOptionInput(inputType: InputType | OptionInputType): boolean {
    if (
      inputType == OptionInputType.Checkbox ||
      inputType == OptionInputType.Dropdown ||
      inputType == OptionInputType.Radio
    )
      return true;
    return false;
  }

  public isDateInput(inputType: InputType | OptionInputType): boolean {
    if (
      inputType == InputType.Week ||
      inputType == InputType.Month ||
      inputType == InputType.MonthYear ||
      inputType == InputType.Date ||
      inputType == InputType.DateTimeLocal
    )
      return true;
    return false;
  }

  private dateFormat(inputType: InputType): string {
    switch (inputType) {
      case InputType.MonthYear:
        return 'MMM YYYY';

      default:
        return 'DD MM YYYY';
    }
  }

  // Custom DatePicker functionality
  chosenYearHandler(normalizedYear: Moment) {
    this.dateInput.setFullYear(moment(normalizedYear).year());
  }

  chosenMonthHandler(
    normalizedMonth: Moment,
    datepicker: MatDatepicker<Moment>,
  ) {
    if (
      !this.formField ||
      this.formField.optionInput ||
      this.formField.type === OptionInputType.Radio ||
      this.formField.type === OptionInputType.Checkbox ||
      this.formField.type === OptionInputType.Dropdown ||
      this.formField.type === OptionInputType.Hidden
    ) {
      datepicker.close();
      return;
    }
    this.dateInput.setMonth(moment(normalizedMonth).month());
    this.formField.value = this.datePipe.transform(
      this.dateInput,
      this.dateFormat(this.formField.type),
    );
    this.CheckValidation();
    datepicker.close();
  }
}
